/**
 * GPX Map Modal — opens a full-screen Leaflet map showing a GPX trace.
 * Leaflet is dynamically imported on first click (zero impact on page weight).
 */

import { loadLeaflet, createBaseLayers } from './leafletCommon.js';

export function initGpxMapModal() {
  document.addEventListener("click", (e) => {
    const trigger = e.target.closest(".gpx-map-trigger");
    if (!trigger) return;

    e.preventDefault();
    e.stopPropagation();

    const track = JSON.parse(trigger.dataset.track);
    const bounds = JSON.parse(trigger.dataset.bounds);
    const gpxName = trigger.dataset.gpxName || "";
    const title = trigger.dataset.mapTitle || "";
    const ignKey = trigger.dataset.ignKey || "";

    openMapModal({ track, bounds, gpxName, title, ignKey });
  });
}

async function openMapModal({ track, bounds, gpxName, title, ignKey }) {
  const L = await loadLeaflet();

  // Create modal overlay
  const overlay = document.createElement("div");
  overlay.className = "gpx-map-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");

  const displayTitle =
    title || gpxName.replace(/\.gpx$/i, "").replace(/[-_]/g, " ");
  overlay.setAttribute("aria-label", displayTitle);

  overlay.innerHTML = `
    <div class="gpx-map-modal">
      <div class="gpx-map-header">
        <button type="button" class="gpx-map-back" aria-label="Back">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 4l-6 6 6 6"/></svg>
        </button>
        <span class="gpx-map-title">${displayTitle}</span>
      </div>
      <div class="gpx-map-container" id="gpx-map-leaflet"></div>
    </div>
  `;

  document.body.appendChild(overlay);
  document.body.style.overflow = "hidden";

  window._paq &&
    window._paq.push([
      "trackEvent",
      "Engagement",
      "Map view",
      displayTitle || gpxName,
    ]);

  const mapContainer = overlay.querySelector("#gpx-map-leaflet");

  // Init Leaflet map
  const map = L.map(mapContainer, {
    zoomControl: true,
    attributionControl: true,
  });

  // Tile layers
  const { baseLayers, defaultLayer } = createBaseLayers(L, ignKey);
  defaultLayer.addTo(map);

  L.control
    .layers(
      baseLayers,
      null,
      { position: "bottomleft" },
    )
    .addTo(map);

  // Draw track polyline
  L.polyline(track, {
    color: "#0369a1",
    weight: 4,
    opacity: 0.9,
  }).addTo(map);

  // Start marker (green)
  if (track.length > 0) {
    L.circleMarker(track[0], {
      radius: 7,
      fillColor: "#16a34a",
      color: "#fff",
      weight: 2,
      fillOpacity: 1,
    }).addTo(map);
  }

  // End marker (red)
  if (track.length > 1) {
    L.circleMarker(track[track.length - 1], {
      radius: 7,
      fillColor: "#dc2626",
      color: "#fff",
      weight: 2,
      fillOpacity: 1,
    }).addTo(map);
  }

  // Fit bounds with padding
  map.fitBounds(bounds, { padding: [30, 30] });

  // invalidateSize after layout settles
  requestAnimationFrame(() => map.invalidateSize());

  // Close handlers
  const close = () => {
    map.remove();
    overlay.remove();
    document.body.style.overflow = "";
    document.removeEventListener("keydown", onKeydown);
  };

  overlay.querySelector(".gpx-map-back").addEventListener("click", close);
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });

  const onKeydown = (e) => {
    if (e.key === "Escape") close();
  };
  document.addEventListener("keydown", onKeydown);
}
